import { useEffect, useState, useId } from 'react'
import mermaid from 'mermaid'
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch'
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'

interface MermaidDiagramDialogProps {
  chart: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

function ZoomControls({ scale }: { scale: number }) {
  const { zoomIn, zoomOut, resetTransform } = useControls()

  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1 rounded-lg border border-border bg-background/95 p-1 shadow-lg backdrop-blur-sm">
      <Button
        variant="ghost"
        size="iconSm"
        onClick={() => zoomOut(0.1)}
        title="Zoom out"
        aria-label="Zoom out"
      >
        <ZoomOut className="h-4 w-4" />
      </Button>
      <span className="min-w-[3.5rem] text-center text-xs font-medium text-muted-foreground select-none">
        {Math.round(scale * 100)}%
      </span>
      <Button
        variant="ghost"
        size="iconSm"
        onClick={() => zoomIn(0.1)}
        title="Zoom in"
        aria-label="Zoom in"
      >
        <ZoomIn className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="iconSm"
        onClick={() => resetTransform()}
        title="Reset zoom"
        aria-label="Reset zoom"
      >
        <RotateCcw className="h-4 w-4" />
      </Button>
    </div>
  )
}

export function MermaidDiagramDialog({
  chart,
  open,
  onOpenChange,
}: MermaidDiagramDialogProps) {
  const [svg, setSvg] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [scale, setScale] = useState(1)
  const id = useId().replace(/:/g, '-')

  useEffect(() => {
    if (!open || !chart.trim()) return

    let cancelled = false

    async function renderDiagram() {
      try {
        const { svg: renderedSvg } = await mermaid.render(
          `mermaid-dialog-${id}`,
          chart.trim()
        )
        if (!cancelled) {
          // Make SVG responsive: remove inline max-width cap, add height="100%"
          // so the SVG fills the dialog via viewBox + preserveAspectRatio
          const responsiveSvg = renderedSvg
            .replace(/<svg[^>]*>/, (tag) =>
              tag.replace(/\sstyle="[^"]*"/, '').replace(/\sheight="[^"]*"/, '')
            )
            .replace(/<svg /, '<svg height="100%" ')
          setSvg(responsiveSvg)
          setError(null)
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Invalid mermaid syntax'
          )
          setSvg('')
        }
      }
    }

    renderDiagram()
    return () => {
      cancelled = true
    }
  }, [open, chart, id])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] max-h-[90vh] flex flex-col p-0 gap-0">
        <VisuallyHidden.Root>
          <DialogTitle>Mermaid Diagram</DialogTitle>
        </VisuallyHidden.Root>
        <div className="relative flex-1 overflow-hidden">
          {error ? (
            <div className="flex items-center justify-center h-full p-8">
              <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 max-w-lg">
                <p className="text-sm font-medium text-destructive">
                  Mermaid diagram error
                </p>
                <pre className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">
                  {error}
                </pre>
              </div>
            </div>
          ) : (
            <TransformWrapper
              initialScale={1}
              minScale={0.5}
              maxScale={5}
              centerOnInit
              wheel={{ step: 0.1 }}
              onTransformed={(_ref, state) => setScale(state.scale)}
            >
              <ZoomControls scale={scale} />
              <TransformComponent
                wrapperStyle={{ width: '100%', height: '100%' }}
                contentStyle={{ width: '100%', height: '100%' }}
              >
                <div
                  className="w-full h-full p-4"
                  dangerouslySetInnerHTML={{ __html: svg }}
                />
              </TransformComponent>
            </TransformWrapper>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
